<template>
  <div>
    <ProgressBar
      v-if="!isLiteView()"
      active-step="Trust SSN and grantor SSN mismatch"
    />
    <PageHeading
      default-heading="Trust SSN and grantor SSN mismatch"
      edit-heading="Trust SSN and grantor SSN mismatch"
    />
    <form
      novalidate="novalidate"
      @submit.prevent
    >
      <div class="pvd-container container-divider">
        <p>
          When using a grantor's SSN for your trust, it must match the SSN of a living grantor on the account.
          Take a moment to review and correct any mistakes on the trust or grantor information you provided.
        </p>
        <h2 class="pvd-heading-S add-margin-1">
                Your trust information
        </h2>
        <div class="table-container">
          <table
            class="pvd-table-simple"
          >
            <thead>
              <tr>
                <th class="text-left-1pl">
                  Name
                </th>
                <th class="text-left-1pl">
                  SSN
                </th>
                <th class="text-left-1pl">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="accountDetails.ssn">
                <td class="text-left-1pl">
                  {{ accountDetails.trustName }}
                </td>
                <td class="text-left-1pl">
                  {{ accountDetails.ssn }}
                </td>
                <td class="text-left-1pl actions-column">
                  <a
                    href="javascript:void(0);"
                    :aria-describedby="`Edit ${accountDetails.trustName}`"
                    @click.prevent="editTrustSSN()"
                  >
                    Edit
                  </a>
                </td>
              </tr>
              <tr v-if="isPrimaryGrantor">
                <div
                  class="display-contents"
                >
                <td class="text-left-1pl">
                  {{ applicant.firstName }} {{ applicant.middleName }} {{ applicant.lastName }}
                </td>
                  <td class="text-left-1pl">
                    {{ applicant.ssn }}
                  </td>
                  <td class="text-left-1pl actions-column">
                    <a v-if="isPrimaryGrantorNotCustomer"
                      href="javascript:void(0);"
                      class="pr-1"
                      :aria-describedby="`Edit ${applicant.firstName} ${applicant.middleName} ${applicant.lastName}`"
                      @click.prevent="editUser('', 'personal-information')"
                    >
                      Edit grantor
                    </a>
                  </td>
                </div>
              </tr>
              <tr
                v-for="(person, index) in additionalRoles"
                :key="index"
              >
                <div
                  v-if="(person.role == 'grantor' && person.isDeceased === 'No') || person.role == 'trustee/grantor'"
                  class="display-contents"
                >
                <td class="text-left-1pl">
                  {{ person.firstName }} {{ person.middleName }} {{ person.lastName }}
                </td>
                  <td class="text-left-1pl">
                    {{ person.ssn }}
                  </td>
                  <td class="text-left-1pl actions-column">
                    <a
                      href="javascript:void(0);"
                      class="pr-1"
                      :aria-describedby="`Edit ${person.firstName} ${person.middleName} ${person.lastName}`"
                      @click.prevent="editUser(index, 'trust-additional-role')"
                    >
                      Edit grantor
                    </a>
                  </td>
                </div>
              </tr>
            </tbody>
          </table>
          <InlineMessage
            v-show="!grantorValid"
            message="You must add at least one grantor to continue."
            css-class="form-row-message p-medium-top p-large-bottom"
          />

          <InlineMessage
            v-show="maxRolesAdded"
            message="You've entered the maximum number of trustees and grantors for this account."
            css-class="form-row-message p-medium-top p-large-bottom"
            type="info"
          />

          <button
            v-if="!maxRolesAdded"
            class="pvd-btn btn-ghost-action add-margin"
            type="button"
            @click="addAnotherRole"
          >
            Add a grantor
          </button>
        </div>
      </div>
    </form>

    <div class="pvd-container pvd-btn-row">
      <p
        class="tertiary-link xsmall"
      >
        <a
          id="exit-overlay-modal-trigger"
          href="javascript:void(0)"
          @click="handleExitClick"
        >{{ pd_content.exit_link.value }}</a>
      </p>
    </div>
    <div
      id="fs-steps"
      class="personal-info-page"
      data-operation="IdentifyUser"
      data-step="Init"
      style="display: none"
    >
      &nbsp;
    </div>
  </div>
</template>

<script>
import ProgressBar from '../components/ProgressBar.vue';
import PageHeading from '../components/PageHeading.vue';
import dataHelper from '../mixins/dataHelper';
import dataSanitize from '../mixins/dataSanitize';
import measurement from '../mixins/measurement';
import utilityFunctions from '../mixins/utility';
import validationHelper from '../mixins/validationHelper';
import widgetHelper from '../mixins/widgetHelper';
import pageNavigationHelper from '../mixins/pageNavigationHelper';

export default {
  name: 'TrustDashboard',
  components: {
    ProgressBar,
    PageHeading,
  },
  mixins: [
    dataHelper,
    dataSanitize,
    measurement,
    utilityFunctions,
    validationHelper,
    widgetHelper,
    pageNavigationHelper,
  ],
  data() {
    return {
      backButtonAttempt: false,
      applicant: {},
      additionalRoles: [],
      accountDetails: {},
      hasAdditionalRoles: false,
      hasAdditionalAuthorities: false,
      grantorValid: true,
      MAX_TRUSTEES: 4,
      MAX_GRANTORS: 6,
      MAX_APPOINT_REMOVE: 6,
      MAX_AMEND_REVOKE: 6,
      activeIndex: null,
      isConfirmationModalOpen: false,
      isLoading: false,
      isPrimaryGrantorNotCustomer: false,
      isPrimaryGrantor: false,
      pageTitle: 'Who else is named in the trust?',
    };
  },
  computed: {
    shouldDisableNext() {
      return this.isLoading ? 'Y' : 'N';
    },

    shouldShowLoadingSpinner() {
      return this.isLoading ? 'Y' : 'N';
    },
    primaryButtonLabel() {
      return this.isLoading ? this.pd_content.primary_button.wait : this.pd_content.primary_button.default;
    },
    maxRolesAdded: {
      get() {
        if (this.applicant && this.applicant.additionalDetails) {
          const roles = [
            this.applicant.additionalDetails.role,
            ...this.additionalRoles.map((person) => person.role),
          ].reduce((acc, role) => {
            let { grantors, trustees } = acc;
            if (role === 'grantor') grantors += 1;
            if (role === 'trustee') trustees += 1;
            if (role === 'trustee/grantor') {
              trustees += 1;
              grantors += 1;
            }
            return { grantors, trustees };
          }, { grantors: 0, trustees: 0 });

          return roles.grantors >= this.MAX_GRANTORS && roles.trustees >= this.MAX_TRUSTEES;
        }
        return false;
      },
    },
    maxAuthoritiesAdded: {
      get() {
        if (this.applicant && this.applicant.additionalDetails) {
          const roles = [
            {
              hasAmendRevoke: this.applicant.additionalDetails.hasAmendRevoke,
              hasAppointRemove: this.applicant.additionalDetails.hasAppointRemove,
            },
            ...this.additionalRoles.map((person) => (
              {
                hasAmendRevoke: person.hasAmendRevoke,
                hasAppointRemove: person.hasAppointRemove,
              }
            )),
          ].reduce((acc, authorities) => {
            let { amendRevoke, appointRemove } = acc;
            const { hasAmendRevoke, hasAppointRemove } = authorities;
            if (hasAmendRevoke) amendRevoke += 1;
            if (hasAppointRemove) appointRemove += 1;
            return { amendRevoke, appointRemove };
          }, { amendRevoke: 0, appointRemove: 0 });

          return roles.amendRevoke >= this.MAX_AMEND_REVOKE && roles.appointRemove >= this.MAX_APPOINT_REMOVE;
        }
        return false;
      },
    },
  },
  mounted() {
    const instanceData = this.getInstanceData();
    this.$store.commit('currentState/updateField', { path: 'stateName', value: 'trust-ssn-mismatch' });
    this.$store.commit('currentState/updateField', { path: 'futureDestinationState', value: '' });
    this.$store.commit('currentState/updateField', { path: 'navAction', value: '' });
    this.$store.commit('currentState/updateField', { path: 'editMode', value: false });
    this.configureBackButtonFunctionality(() => this.$emit('invoke-previous'));
    this.removeModalClassIfExists();
    this.configureBackButtonFunctionality(() => this.$emit('invoke-previous'));
    document.title = this.setTitle(this.pageTitle);
    this.applicant = instanceData.formData.primaryUser;
    this.isPrimaryGrantorNotCustomer = this.applicant.additionalDetails.role === 'trustee/grantor' && this.applicant?.flowType !== 'Express';
    this.isPrimaryGrantor = this.applicant.additionalDetails.role === 'trustee/grantor';
    this.additionalRoles = instanceData.formData.additionalUsers;
    this.accountDetails = instanceData.formData.accountDetails;
    this.hasAdditionalRoles = this.additionalRoles.filter((person) => person.role !== 'authority').length > 0;
    this.hasAdditionalAuthorities = this.additionalRoles.filter((person) => person.role === 'authority').length > 0;
    this.isLoading = false;
  },

  methods: {
    nextButtonClicked() {
      this.isLoading = true;
      this.grantorValid = true;
      if (
        (this.additionalRoles.some((person) => person.role === 'grantor' || person.role === 'trustee/grantor'))
        || (this.applicant.additionalDetails.role === 'grantor' || this.applicant.additionalDetails.role === 'trustee/grantor')
      ) {
        this.$store.commit('currentState/updateField', { path: 'destinationState', value: 'trust-review-and-confirm' });
        this.performNavigation(this.pd_content.primary_button.value);
      } else {
        this.grantorValid = false;
        this.isLoading = false;
      }
    },
    addAnotherRole() {
      this.$store.commit('currentState/updateField', { path: 'currentUserIndex', value: this.getInstanceData().formData.additionalUsers.length });
      this.$store.commit('currentState/updateField', { path: 'destinationState', value: 'trust-additional-role' });
      this.$store.commit('currentState/updateField', { path: 'futureDestinationState', value: 'trust-dashboard' });
      this.$store.commit('currentState/updateField', { path: 'editMode', value: false });
      this.performNavigation(this.pd_content.primary_button.value);
    },
    handleExitClick() {
      this.track('exit-link', 'interaction');
    },
    addAnotherAuthority() {
      this.$store.commit('currentState/updateField', { path: 'destinationState', value: 'trust-authority' });
      this.performNavigation(this.pd_content.primary_button.value);
    },
    openConfirmationModal(index) {
      this.setActiveIndex(index);
      this.isConfirmationModalOpen = true;
    },
    setActiveIndex(index) {
      this.activeIndex = index;
    },
    closeConfirmationModal() {
      this.isConfirmationModalOpen = false;
      this.setActiveIndex(null);
    },
    removeUser(index) {
      if (this.getInstanceData() && this.getInstanceData().formData && this.getInstanceData().formData.additionalUsers) {
        const { additionalUsers } = this.getInstanceData().formData;
        this.setListInstanceData('formData', [...additionalUsers.slice(0, index), ...additionalUsers.slice(index + 1)], 'additionalUsers');

        this.additionalRoles = this.getInstanceData().formData.additionalUsers;
        this.hasAdditionalRoles = this.additionalRoles.filter((person) => person.role !== 'authority').length > 0;
        this.hasAdditionalAuthorities = this.additionalRoles.filter((person) => person.role === 'authority').length > 0;
      }
      this.closeConfirmationModal();
    },
    editUser(index, page) {
      this.$store.commit('currentState/updateField', { path: 'destinationState', value: page });
      this.$store.commit('currentState/updateField', { path: 'editMode', value: true });
      this.$store.commit('currentState/updateField', { path: 'navAction', value: '' });
      this.$store.commit('currentState/updateField', { path: 'userIndex', value: index });
      this.$store.commit('currentState/updateField', { path: 'currentUserIndex', value: index });
      this.$store.commit('currentState/updateField', { path: 'futureDestinationState', value: 'trust-ssn-mismatch' });
      this.routeEngine(this.getInstanceData());
    },
    editTrustSSN() {
      this.$store.commit('currentState/updateField', { path: 'destinationState', value: 'trust-information' });
      this.$store.commit('currentState/updateField', { path: 'navAction', value: 'next' });
      this.$store.commit('currentState/updateField', { path: 'futureDestinationState', value: 'trust-ssn-mismatch' });
      this.$store.commit('currentState/updateField', { path: 'editMode', value: true });
      this.routeEngine(this.getInstanceData());
    },
    formatRole(role) {
      const preFormat = role === 'trustee/grantor' ? 'trustee and grantor' : role;
      return preFormat.charAt(0).toUpperCase() + preFormat.slice(1);
    },
  },
};

</script>

<style scoped>
  .table-container {
    margin-top: 1rem;
  }
  .pvd-table-simple {
    width: 100%;
  }
  .display-contents {
     display: contents;
  }
  .add-margin {
    margin-top: 1.5em;
  }
  .add-margin-1 {
    margin-top: 1em;
  }
  .text-left-1pl {
    text-align: left !important;
    padding-left: 1em;
  }
  .pr-1 {
    padding-right: 1rem;
  }
  .actions-column {
    min-width: 130px;
    width: 130px;
  }
  table {
    border-collapse: collapse;
  }
  table th {
    border-top: 1px solid #ccc;
    border-left: 1px solid #ccc;
    padding-top: 1.5em;
    padding-bottom: 0.75em;
  }

  table th:last-child {
    border-top: 1px solid #ccc;
    border-right: 1px solid #ccc;
    border-left: 1px solid #ccc;
    padding-top: 1.5em;
    padding-bottom: 0.75em;
  }

  table td {
    border-top: 1px solid #ccc;
    border-bottom: 1px solid #ccc;
    border-left: 1px solid #ccc;
    padding-top: 1.5em;
    padding-bottom: 1.5em;
  }

  table td:last-child {
    border-top: 1px solid #ccc;
    border-bottom: 1px solid #ccc;
    border-right: 1px solid #ccc;
    border-left: 1px solid #ccc;
    padding-top: 1.5em;
    padding-bottom: 1.5em;
  }
</style>
